import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import Box from "@mui/material/Box";
import MuiDialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";

import { withTranslation } from "react-i18next";
import { isEmpty } from "lodash";

import fpResourceService from "@plannel/services/fp/fp-resource-service";
import FpGridSetting from "@plannel/pages/factory-plan/common/grid";
import dateUtils from "@plannel/utils/date-util";
import debug from "@plannel/utils/debug-util";

/**
 * 자원(Resource) 조회/선택 전용 팝업 — old `ResourcePopup.jsx` 대응(ItemPopup.js와 동일 구성,
 * 더블클릭 선택). 자원 자체 관리는 Resource.js에서 함.
 *
 * props: open, onClose(), planScope, onSelect({resourceCd, resourceNm})
 */
const ResourcePopup = ({ t, open, onClose, planScope, onSelect }) => {
  const gridRef = useRef();
  const [rowData, setRowData] = useState([]);
  const gridOptions = useMemo(() => FpGridSetting({ title: t("FP_RESOURCE_SELECT"), viewName: "" }), [t]);

  const columnDefs = useMemo(() => ([
    { headerName: t("FP_RESOURCE_CD"), field: "resourceCd", colId: "resourceCd", width: 180, editable: false },
    { headerName: t("FP_RESOURCE_NM"), field: "resourceNm", colId: "resourceNm", width: 220, editable: false },
    { headerName: t("FP_DESC_TXT"), field: "descTxt", colId: "descTxt", width: 150, editable: false },
    {
      headerName: t("FP_COL_AUDIT"), groupId: "audit", marryChildren: true, openByDefault: false,
      children: [
        { headerName: t("updatedTs"), field: "modifyDttm", colId: "modifyDttm", type: ["nonEditableColumn", "centerAligned"], width: 150, valueFormatter: dateUtils.formatDateTime },
        { headerName: t("updatedBy"), field: "modifyUser", colId: "modifyUser", type: ["nonEditableColumn", "centerAligned"], width: 110, columnGroupShow: "open" },
        { headerName: t("createdTs"), field: "createDttm", colId: "createDttm", type: ["nonEditableColumn", "centerAligned"], width: 150, valueFormatter: dateUtils.formatDateTime, columnGroupShow: "open" },
        { headerName: t("createdBy"), field: "createUser", colId: "createUser", type: ["nonEditableColumn", "centerAligned"], width: 110, columnGroupShow: "open" }
      ]
    }
  ]), [t]);

  const loadResources = () => {
    if (!planScope) return;
    fpResourceService.getAll({ planScope })
      .then(response => setRowData(isEmpty(response.data) ? [] : response.data))
      .catch(e => { debug(e); setRowData([]); });
  };

  useEffect(() => {
    if (!open || !planScope) return;
    loadResources();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, planScope]);

  const onRowDoubleClicked = (e) => {
    if (!e.data) return;
    onSelect({ resourceCd: e.data.resourceCd, resourceNm: e.data.resourceNm });
    onClose();
  };

  return (
    <MuiDialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{t("FP_RESOURCE_SELECT")}</DialogTitle>
      <DialogContent>
        <Box className="ag-theme-balham" sx={{ height: 440, width: "100%" }}>
          <AgGridReact
            ref={gridRef}
            rowData={rowData}
            columnDefs={columnDefs}
            onRowDoubleClicked={onRowDoubleClicked}
            {...gridOptions}
            rowSelection={{ mode: "singleRow", checkboxes: false, enableClickSelection: true }}
          />
        </Box>
      </DialogContent>
    </MuiDialog>
  );
};

export default withTranslation()(ResourcePopup);
